<template>
    <persistent-banner
        v-if="onSampleData"
        class="sample-data__banner"
        background-color="var(--palette-gray-0)"
        text-color="var(--palette-gray-100)">
        <pendo-icon
            class="sample-data__icon"
            type="chart-area"
            stroke-width="2.5"
            size="16" />
        <div>
            <div class="sample-data__header">
                You're in demo mode, powered by sample data.
            </div>
            <div class="sample-data__caption">
                To view data for your app, connect to Pendo.
            </div>
        </div>
        <pendo-button
            class="sample-data__button"
            data-cy="disable-demo-mode-button"
            theme="app"
            type="secondary"
            label="Disable Demo Mode"
            size="mini"
            @click="switchSubs" />
    </persistent-banner>
</template>

<script>
import { FREE_CONSTANTS } from '@/constants';
import { getSampleDataSubName } from '@/free/free.utils.js';
import { PendoButton, PendoIcon } from '@pendo/components';
import get from 'lodash/get';
import invoke from 'lodash/invoke';
import { mapGetters, mapState } from 'vuex';
import PersistentBanner from '../../common/persistent-banner/persistent-banner.vue';
import { EventBus } from '@/utils/event-bus';

export function toggleSampleDataTooltip (visible = true) {
    EventBus.$emit('toggleSampleDataTooltip', visible);
}

export default {
    name: 'SampleDataBanner',
    components: {
        PersistentBanner,
        PendoButton,
        PendoIcon,
    },
    computed: {
        ...mapState({
            subscription: (state) => state.subscription.active,
        }),
        ...mapGetters({
            subscriptions: 'subscription/list',
            sampleDataSub: 'subscription/sampleDataSubscription',
            defaultSubscription: 'subscription/defaultSubscription',
            preSampleDataSubId: 'auth/preSampleDataSubId',
            getFeatureFlag: 'subscription/getFeatureFlag',
        }),
        onSampleData () {
            return this.getFeatureFlag('sampleDataSubscription');
        },
        sampleDataSubName () {
            return getSampleDataSubName();
        },
    },
    created () {
        EventBus.$on('toggleSampleDataTooltip', this.toggleSampleDataTooltip);
        this.sampleDataTooltipDismissed =
            window.localStorage.getItem(FREE_CONSTANTS.SampleDataTooltipDismissedKey) === 'true';
    },
    beforeUnmount () {
        EventBus.$off('toggleSampleDataTooltip', this.toggleSampleDataTooltip);
    },
    methods: {
        async switchSubs () {
            const eventData = {
                location: window.location.pathname,
            };

            if (this.onSampleData) {
                const preSampleDataSub = this.subscriptions.find((sub) => sub.id === this.preSampleDataSubId);
                eventData.from = this.sampleDataSubName;
                eventData.to = get(preSampleDataSub, 'name', '');
                window.localStorage.removeItem(FREE_CONSTANTS.SampleDataLocalStorageKey);
            } else {
                eventData.from = this.subscription.name;
                eventData.to = this.sampleDataSubName;
                this.sampleDataTooltipDismissed = false;
                window.localStorage.removeItem(FREE_CONSTANTS.SampleDataTooltipDismissedKey);
                window.localStorage.setItem(FREE_CONSTANTS.SampleDataLocalStorageKey, true);
            }

            invoke(window, 'pendo.track', 'sample-data-toggle', eventData);

            const activeSubId = this.onSampleData
                ? this.preSampleDataSubId || this.defaultSubscription.id
                : this.sampleDataSub.id;
            await this.$router.pendo.switchSubscription(activeSubId);
        },
        toggleSampleDataTooltip (visible) {
            if (visible) {
                this.sampleDataTooltipDismissed = false;
                window.localStorage.removeItem(FREE_CONSTANTS.SampleDataTooltipDismissedKey);
            } else {
                this.sampleDataTooltipDismissed = true;
                window.localStorage.setItem(FREE_CONSTANTS.SampleDataTooltipDismissedKey, 'true');
            }
        },
    },
};
</script>

<style scoped lang="less">
.sample-data {
    &__banner {
        border-top: 3px solid var(--palette-purple-50);
        border-bottom: 1px solid var(--palette-gray-40);
        box-shadow: 0px 2px 6px var(--semantic-drop-shadow-base);
        box-sizing: border-box;
        margin-bottom: 6px;
    }

    &__icon {
        margin-right: 16px;
        color: var(--palette-purple-50);
        background-color: var(--palette-purple-10);
        padding: 10px;
        border-radius: 32px;
    }

    &__header,
    &__caption {
        line-height: 130%;
    }

    &__caption {
        font-weight: normal;
    }

    &__button {
        margin-left: 16px;
        font-size: 12px;
        font-weight: 600;
    }
}
</style>
